import { createFileRoute } from "@tanstack/react-router";
import { PageImageGallery } from "@/components/page-image-gallery";
import { SITE } from "@/lib/site";
import { ogImageMeta } from "@/lib/og";

export const Route = createFileRoute("/privacybeleid")({
  head: () => ({
    meta: [
      { title: `Privacybeleid & Cookies | ${SITE.name}` },
      {
        name: "description",
        content:
          "Lees onze complete privacy- en cookie-uitleg: welke gegevens Casino Betalingen verwerkt, welke cookies we plaatsen en hoe je voorkeuren beheert.",
      },
      { property: "og:title", content: `Privacybeleid & Cookies | ${SITE.name}` },
      {
        property: "og:description",
        content:
          "De complete uitleg over privacy, cookies en persoonsgegevens bij Casino Betalingen, bijgewerkt in november 2026.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
      ...ogImageMeta("privacybeleid"),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/privacybeleid/` }],
  }),
  component: PrivacybeleidPage,
});

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-10">
      <h2 className="text-xl font-bold tracking-tight text-foreground">{title}</h2>
      <div className="prose-nl mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">
        {children}
      </div>
    </section>
  );
}

function PrivacybeleidPage() {
  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6">
      <nav aria-label="Kruimelpad" className="mb-6 text-xs text-muted-foreground">
        <a href="/" className="hover:text-primary hover:underline">Home</a>
        <span className="mx-2">/</span>
        <span>Privacybeleid</span>
      </nav>

      <h1 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
        Privacybeleid &amp; cookies
      </h1>
      <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
        Laatst bijgewerkt: 1 november 2026 · {SITE.name} ({SITE.domain})
      </p>

      <Section title="Wie wij zijn">
        <p>
          {SITE.name} is een onafhankelijke vergelijkingssite voor
          betaalmethoden bij online casino's, gericht op de Nederlandse markt
          (18+). Wij verkopen zelf geen kansspelen en verwerken geen
          spelersaccounts of tegoeden.
        </p>
      </Section>

      <Section title="Welke cookies we plaatsen">
        <p>
          Deze site plaatst geen tracking- of marketingcookies; daarom tonen we
          geen cookiemelding. Bezoekersstatistieken komen anoniem uit onze
          hosting en werken zonder cookies.
        </p>
        <p>
          Alleen je filterkeuzes in de casinozoeker kunnen lokaal in je eigen
          browser worden bewaard. Die gegevens verlaten je apparaat niet.
        </p>
      </Section>

      <Section title="Affiliate-links">
        <p>
          Knoppen zoals "Bezoek casino" leiden via een affiliate-link naar de
          aanbieder. Als je doorklikt, kan de aanbieder een eigen cookie
          plaatsen om de verwijzing toe te kennen. Daarvoor geldt het
          privacybeleid van de betreffende aanbieder. Klikken kost jou niets
          extra's.
        </p>
      </Section>

      <Section title="Welke gegevens we verwerken">
        <p>
          Je kunt de hele site gebruiken zonder account. Wij verwerken geen
          namen, adressen of betaalgegevens van bezoekers. De casinozoeker
          werkt volledig in je eigen browser; je filterkeuzes worden nergens
          heen gestuurd.
        </p>
      </Section>

      <Section title="Je rechten">
        <p>
          Je hebt recht op inzage, correctie en verwijdering van
          persoonsgegevens. Omdat wij vrijwel geen persoonsgegevens opslaan,
          is er meestal niets om in te zien of te verwijderen. Lokaal
          bewaarde filters wis je door de site-data van {SITE.domain} in je
          browser te verwijderen.
        </p>
      </Section>

      <Section title="Wijzigingen">
        <p>
          Dit beleid kan worden aangepast, bijvoorbeeld bij nieuwe functies op
          de site. De actuele versie staat altijd op deze pagina.
        </p>
      </Section>
          <PageImageGallery path="/privacybeleid" title="Privacy bij Casino Betalingen" />
    </main>
  );
}
